import {
  Children,
  type ReactElement,
  type ReactNode,
  isValidElement,
  useState,
} from 'react';

import { RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react';
import classNames from 'classnames';

import { ButtonIcon } from '@spotify-internal/encore-web';

import styles from './Carousel.module.css';

export const CAROUSEL_ICON_SIZE = 16;

export type CarouselProps = {
  ariaLabel: string;
  children: ReactNode;
  className?: string;
  itemName: string;
  onSlideChange?: (index: number) => void;
};

export const Carousel = ({
  ariaLabel,
  children,
  className,
  itemName,
  onSlideChange,
}: CarouselProps): ReactElement | null => {
  const slides = Children.toArray(children);
  const [selectedIndex, setSelectedIndex] = useState(0);

  if (slides.length === 0) {
    return null;
  }

  const activeIndex = Math.min(selectedIndex, slides.length - 1);
  const hasMultipleSlides = slides.length > 1;
  const capitalizedItemName = `${itemName.charAt(0).toUpperCase()}${itemName.slice(
    1,
  )}`;

  return (
    <div
      className={classNames(styles.root, className)}
      role="group"
      aria-label={ariaLabel}
      aria-roledescription="carousel"
    >
      <div className={styles.previous}>
        {hasMultipleSlides && (
          <ButtonIcon
            variant="tertiary"
            size="small"
            aria-label={`Previous ${itemName}`}
            disabled={activeIndex === 0}
            onClick={() => {
              const next = Math.max(0, activeIndex - 1);
              setSelectedIndex(next);
              onSlideChange?.(next);
            }}
            data-track-id={`button.previous-${itemName}`}
            data-track-action="navigate"
            data-track-data-direction="previous"
          >
            <RiArrowLeftSLine size={CAROUSEL_ICON_SIZE} aria-hidden />
          </ButtonIcon>
        )}
      </div>
      <div className={styles.viewport}>
        <div
          className={styles.track}
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {slides.map((slide, index) => {
            const isActive = index === activeIndex;
            const key = isValidElement(slide) ? slide.key : index;

            return (
              <div
                key={key}
                className={styles.slide}
                role="group"
                aria-label={`${capitalizedItemName} ${index + 1} of ${
                  slides.length
                }`}
                aria-roledescription="slide"
                aria-hidden={isActive ? undefined : true}
                ref={element => {
                  element?.toggleAttribute('inert', !isActive);
                }}
              >
                {slide}
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.next}>
        {hasMultipleSlides && (
          <ButtonIcon
            variant="tertiary"
            size="small"
            aria-label={`Next ${itemName}`}
            disabled={activeIndex === slides.length - 1}
            onClick={() => {
              const next = Math.min(slides.length - 1, activeIndex + 1);
              setSelectedIndex(next);
              onSlideChange?.(next);
            }}
            data-track-id={`button.next-${itemName}`}
            data-track-action="navigate"
            data-track-data-direction="next"
          >
            <RiArrowRightSLine size={CAROUSEL_ICON_SIZE} aria-hidden />
          </ButtonIcon>
        )}
      </div>
      {hasMultipleSlides && (
        <div
          className={styles.dots}
          role="status"
          aria-label={`${capitalizedItemName} ${activeIndex + 1} of ${
            slides.length
          }`}
        >
          {slides.map((slide, index) => (
            <span
              key={isValidElement(slide) ? slide.key : index}
              className={styles.dot}
              data-active={index === activeIndex}
              aria-hidden
            />
          ))}
        </div>
      )}
    </div>
  );
};
